import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classes from './TextField.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import t from '@/utils/Language/UI-translations';
import { formatPhoneNumber } from '@/utils/helpers/textfield';

const formatInput = (format, value) => {
  if (format === 'phone') {
    value = formatPhoneNumber(value);
  }
  return value;
};

/**
 * TextField UI
 * @param label Label text of the component
 * @param placeholder Placeholder text of the component
 * @param type Types [default | editable | long]
 * @param subtype Types [left | center | right]
 * @param labelType Label Types [overline | inline]
 * @param errorMessage
 * @param format field format [phone]. It can be extended to other formats as well whenever it is required.
 * @param additionalClasses String with classes from the components using the TextField (optional)
 */
function TextField({
  label,
  value,
  placeholder,
  errorMessage,
  required = false,
  requiredLabel,
  type = 'default',
  subtype = '',
  labelType = 'overline',
  border,
  format,
  additionalClasses,
  additionalLabelClass,
  additionalEditableIconClass,
  ...props
}) {
  const defaultFontStyle = 'fas';
  const [inputValue, setInputValue] = useState(value ? formatInput(format, value) : '');
  const [placeholderText, setPlaceholderText] = useState();
  const [requiredLabelText, setRequiredLabelText] = useState();

  // Default styles for TextField
  const defaultTextFieldStyles = {
    shape: 'pill',
    width: 'auto'
  };

  // Change event on input field
  const onInputChange = (event) => {
    let textValue = formatInput(format, event.target.value);
    setInputValue(textValue);
    if (props.onChange) {
      props.onChange(textValue);
    }
  };

  // Clear input field
  function onClearInput() {
    setInputValue('');
    if (props.onClear) {
      props.onClear('');
    }
  }

  function onIconClick() {
    if (inputValue === '') {
      return;
    }
    if (props.icon.onClick) {
      props.icon.onClick();
    }
  }

  // Checks whether error is present on this field
  const isError = () => {
    return errorMessage && errorMessage !== '';
  };

  // classNames for elements.
  // Note: defined classes for those elements which are having muliple (>2) and conditional based classes
  const leftIconClasses = [
    classes.labelLeft,
    isError() ? classes.error : '',
    props.icon ? classes.hasIcon : '',
    additionalLabelClass ? additionalLabelClass : ''
  ]
    .map((e) => e)
    .join(' ');

  const inputClasses = [
    'baseClass',
    `${type}`,
    `${subtype}`,
    props.shape ? props.shape : defaultTextFieldStyles.shape,
    isError() ? 'has-error' : '',
    labelType ? `hasLabel-${labelType}` : '',
    border ? 'border' : '',
    props.icon ? 'hasIcon' : '',
    props.width ? props.width : '',
    props.clear ? 'has-clear' : '',
    props.maxWidth ? 'max-width' : '',
    props.icon?.position === 'right' ? 'right-icon' : 'left-icon'
  ]
    .map((e) => classes[e])
    .join(' ');

  useEffect(() => {
    // This is the example, to set default values for UI component
    setPlaceholderText(placeholder || 'Example Value');
    setRequiredLabelText(requiredLabel || 'Required Field');
  }, [placeholder, requiredLabel]);

  return (
    <div style={props.styles} className={classes.textFieldContainer}>
      {required ? <div className={`${classes['labelRequired']} ${classes['error']}`}>* {requiredLabelText}</div> : ''}
      {label && <div className={leftIconClasses}>{label}</div>}
      {props.icon && ['left', undefined].includes(props?.icon?.position) ? (
        <FontAwesomeIcon
          icon={[`${props?.icon?.style}`, `${props?.icon?.font}`]}
          className={`${classes[props?.icon?.style]} ${classes.iconLeft} ${props?.icon?.additionalSearchBoxIconClass}`}
        />
      ) : (
        ''
      )}
      {type === 'long' ? (
        <textarea
          id={props.id}
          placeholder={t(placeholderText)}
          row="20"
          className={inputClasses}
          onChange={onInputChange}
          value={inputValue}
          ref={props.ref}
          maxLength={props?.maxLength}
        ></textarea>
      ) : (
        <input
          data-cy={`input-${props.id}`}
          id={props.id}
          name={props.name}
          placeholder={t(placeholderText)}
          className={`${inputClasses} ${additionalClasses}`}
          onChange={onInputChange}
          value={inputValue}
          ref={props.ref}
          maxLength={props?.maxLength}
        />
      )}
      {props.icon && props.icon.position === 'right' ? (
        <FontAwesomeIcon
          icon={[`${props?.icon?.style}`, `${props?.icon?.font}`]}
          className={`${classes[defaultFontStyle]} ${classes.iconRight} ${props?.icon?.additionalIconClass}`}
          onClick={onIconClick}
        />
      ) : (
        ''
      )}
      {props.clear && inputValue && inputValue !== '' ? (
        <FontAwesomeIcon
          data-cy="modal-client-search-input-clear"
          icon={[defaultFontStyle, `circle-xmark`]}
          className={`${classes[defaultFontStyle]} ${classes.clearInput} ${props?.icon?.additionalSearchBoxIconClass}`}
          onClick={onClearInput}
        />
      ) : (
        ''
      )}
      {type === 'editable' ? (
        <svg
          className={`${classes.textFieldEditable} ${additionalEditableIconClass ? additionalEditableIconClass : ''}`}
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M3.68333 17.1492C3.44956 17.1488 3.22672 17.0502 3.06916 16.8775C2.9087 16.7062 2.82896 16.4746 2.84999 16.2408L3.05416 13.9958L12.4858 4.5675L15.4333 7.51417L6.00416 16.9417L3.75916 17.1458C3.73333 17.1483 3.70749 17.1492 3.68333 17.1492ZM16.0217 6.925L13.075 3.97834L14.8425 2.21084C14.9988 2.05436 15.2109 1.96643 15.4321 1.96643C15.6533 1.96643 15.8654 2.05436 16.0217 2.21084L17.7892 3.97834C17.9456 4.13464 18.0336 4.34675 18.0336 4.56792C18.0336 4.78909 17.9456 5.0012 17.7892 5.1575L16.0225 6.92417L16.0217 6.925Z" />
        </svg>
      ) : (
        ''
      )}
      {isError() ? <div className={`${classes.errorMessage} ${classes['error']}`}>{errorMessage}</div> : ''}
    </div>
  );
}

TextField.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  placeholder: PropTypes.string,
  type: PropTypes.string,
  errorMessage: PropTypes.string,
  labelType: PropTypes.string,
  required: PropTypes.bool,
  requiredLabel: PropTypes.string
};

export default TextField;
